import React, { useMemo, useState } from 'react';
import { platformSliceOf } from './PlatformDonutChart';
import { PLATFORM_DESCRIPTIONS, shortPlatformName } from './platforms';

/**
 * UpSet view of the platform combinations: one row per platform (with its total
 * as a bar on the left), one column per exact combination that occurs, sorted
 * by size. Filled dots mark the platforms in a column's combination, and the
 * bar above it is the number of proteins detected on exactly those platforms.
 * Clicking a column toggles that combination as a filter.
 *
 * Rows are the platform families; SomaScan 7k is not a row of its own, since it
 * is a subset of SomaScan rather than a separate platform.
 */

const FAMILIES = ['MS', 'SomaScan', 'Olink', 'MSD'];

// Whether the platform descriptions under the chart are shown: a per-browser
// preference, so it may be unavailable (private mode) -- default to shown.
const DESCRIPTIONS_KEY = 'saa.upset.platformDescriptions';
function readShowDescriptions() {
  try { return localStorage.getItem(DESCRIPTIONS_KEY) !== 'hidden'; } catch { return true; }
}
function writeShowDescriptions(show) {
  try { localStorage.setItem(DESCRIPTIONS_KEY, show ? 'shown' : 'hidden'); } catch { /* not persisted */ }
}

// Smallest drawn bar height, so a combination of one protein is still a bar.
const MIN_BAR = 3;

// The family combination a protein belongs to ("MS + Olink"), or null.
export function platformComboOf(keys) {
  const slice = platformSliceOf(keys);
  return slice ? slice.split('|')[0] : null;
}

// "MS", "MS and Olink", "MS, SomaScan and Olink".
function platformList(combo) {
  const names = combo.split(' + ');
  return names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function upsetRowPasses(keys, selectedCombos, selectedRows) {
  if (selectedCombos.size === 0 && selectedRows.size === 0) return true;
  const combo = platformComboOf(keys);
  if (!combo) return false;
  return selectedCombos.has(combo) || combo.split(' + ').some(f => selectedRows.has(f));
}

/**
 * Selection is two sets, combined as a union: columns picked one by one
 * (`selected`), and platforms picked by their row (`selectedRows`), each of
 * which covers every column it is part of. Keeping rows as their own state
 * means clearing one platform never undoes what another platform selected.
 */
export default function PlatformUpSet({ rowData, selected, selectedRows, onToggle, onToggleRow, isDark }) {
  const [showDescriptions, setShowDescriptions] = useState(readShowDescriptions);
  const toggleDescriptions = () => {
    setShowDescriptions(prev => { writeShowDescriptions(!prev); return !prev; });
  };

  const { combos, setSizes } = useMemo(() => {
    const byCombo = new Map();
    const sizes = Object.fromEntries(FAMILIES.map(f => [f, 0]));
    for (const row of rowData || []) {
      const combo = platformComboOf(row.platformKeys);
      if (!combo) continue;
      byCombo.set(combo, (byCombo.get(combo) || 0) + 1);
      combo.split(' + ').forEach(f => { sizes[f] += 1; });
    }
    const list = Array.from(byCombo, ([id, count]) => ({ id, count, members: new Set(id.split(' + ')) }))
      .sort((a, b) => b.count - a.count || a.members.size - b.members.size);
    return { combos: list, setSizes: sizes };
  }, [rowData]);

  // The platform names actually recorded in the data, per family, with how
  // many proteins carry each: "SomaScan 7k (216)", "Olink Target 96 ...".
  const variants = useMemo(() => {
    const byFamily = {};
    for (const row of rowData || []) {
      const names = String(row.platforms || '').split(',').map(s => s.trim()).filter(Boolean);
      for (const name of names) {
        const fam = shortPlatformName(name);
        byFamily[fam] = byFamily[fam] || new Map();
        byFamily[fam].set(name, (byFamily[fam].get(name) || 0) + 1);
      }
    }
    return byFamily;
  }, [rowData]);

  const rows = FAMILIES.filter(f => setSizes[f] > 0);
  if (combos.length === 0) return <div style={{ textAlign: 'center', opacity: 0.7 }}>No platform data</div>;

  const fg = isDark ? '#ddd' : '#222';
  const muted = isDark ? '#555' : '#d4d4d4';
  const bar = isDark ? '#9db8e0' : '#3a5f94';
  const selColor = isDark ? '#ffe17a' : '#fb923c';
  const stripe = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.035)';

  // Geometry (SVG units; the drawing scales to the container width).
  const colW = 40;
  const rowH = 30;
  const topH = 170;           // intersection bars, plus room for their counts
  const setBarW = 130;        // set-size bars, growing leftwards
  const labelW = 90;
  const x0 = setBarW + labelW + 10; // left edge of the matrix
  const y0 = topH + 8;              // top edge of the matrix
  const matrixBottom = y0 + rows.length * rowH;
  const nameH = 135;          // vertical combination names under the matrix
  const W = x0 + combos.length * colW + 10;
  const H = matrixBottom + nameH;

  const maxCombo = Math.max(...combos.map(c => c.count));
  const maxSet = Math.max(...rows.map(r => setSizes[r]));
  const barH = c => Math.max(MIN_BAR, (c.count / maxCombo) * (topH - 24));
  const colX = i => x0 + i * colW + colW / 2;
  const rowY = j => y0 + j * rowH + rowH / 2;

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <div style={{ textAlign: 'center', fontSize: '0.8rem', opacity: 0.75, marginBottom: 6 }}>
        Each column counts the proteins detected only on the platforms marked by its dots. Click a column to filter by it, or a platform to select all its columns.
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', width: '100%', maxWidth: W * 1.25, margin: '0 auto', fontFamily: 'inherit' }}
        role="group"
        aria-label="Platform combinations (UpSet)"
      >
        {/* Row stripes, then per platform its total bar and name. Clicking the
            bar or the name selects every column the platform is part of, i.e.
            all proteins detected on it; clicking again clears them. */}
        {rows.map((r, j) => (j % 2 === 0 ? (
          <rect key={`stripe-${r}`} x={x0} y={y0 + j * rowH} width={combos.length * colW} height={rowH} fill={stripe} />
        ) : null))}
        {rows.map((r, j) => {
          const w = (setSizes[r] / maxSet) * (setBarW - 40);
          const allOn = selectedRows.has(r);
          return (
            <g
              key={r}
              onClick={() => onToggleRow(r, !allOn)}
              style={{ cursor: 'pointer' }}
            >
              <title>{`${setSizes[r]} ${setSizes[r] === 1 ? 'protein' : 'proteins'} detected on ${r}, alone or with other platforms\nClick to ${allOn ? 'remove' : 'apply'} filter`}</title>
              {/* Hit area for the bar, the count and the name */}
              <rect x={0} y={y0 + j * rowH} width={x0} height={rowH} fill="transparent" />
              <rect x={setBarW - w} y={rowY(j) - 7} width={Math.max(w, MIN_BAR)} height={14}
                fill={allOn ? selColor : bar} rx={2}
                stroke={allOn ? fg : 'none'} strokeWidth={allOn ? 1.5 : 0} />
              <text x={setBarW - w - 4} y={rowY(j)} dy="0.32em" textAnchor="end" fontSize={11} fill={fg}
                opacity={allOn ? 1 : 0.75} fontWeight={allOn ? 700 : 400}>
                {setSizes[r]}
              </text>
              <text x={x0 - 10} y={rowY(j)} dy="0.32em" textAnchor="end" fontSize={13} fontWeight={allOn ? 800 : 600} fill={fg}>
                {r}
              </text>
            </g>
          );
        })}

        {/* One clickable column per combination */}
        {combos.map((c, i) => {
          // Selected on its own, or covered by a selected platform row.
          const coveredBy = Array.from(c.members).filter(f => selectedRows.has(f));
          const explicit = selected.has(c.id);
          const on = explicit || coveredBy.length > 0;
          const locked = !explicit && coveredBy.length > 0;
          const h = barH(c);
          const memberRows = rows.map((r, j) => (c.members.has(r) ? j : -1)).filter(j => j >= 0);
          const color = on ? selColor : bar;
          return (
            <g
              key={c.id}
              onClick={() => { if (!locked) onToggle(c.id, !explicit); }}
              style={{ cursor: locked ? 'default' : 'pointer' }}
            >
              <title>{`${c.count} ${c.count === 1 ? 'protein' : 'proteins'} detected only on ${platformList(c.id)}\n${
                locked
                  ? `Included by the ${platformList(coveredBy.join(' + '))} selection`
                  : `Click to ${explicit ? 'remove' : 'apply'} filter`}`}</title>
              {/* Hit area for the whole column */}
              <rect x={x0 + i * colW} y={0} width={colW} height={H} fill="transparent" />
              <rect x={colX(i) - 12} y={topH - h} width={24} height={h} fill={color} rx={2}
                stroke={on ? fg : 'none'} strokeWidth={on ? 1.5 : 0} />
              <text x={colX(i)} y={topH - h - 5} textAnchor="middle" fontSize={11}
                fontWeight={on ? 700 : 500} fill={fg}>
                {c.count}
              </text>
              {memberRows.length > 1 && (
                <line x1={colX(i)} x2={colX(i)} y1={rowY(memberRows[0])} y2={rowY(memberRows[memberRows.length - 1])}
                  stroke={color} strokeWidth={3} />
              )}
              {rows.map((r, j) => (
                <circle key={r} cx={colX(i)} cy={rowY(j)} r={7}
                  fill={c.members.has(r) ? color : muted}
                  stroke={on && c.members.has(r) ? fg : 'none'} strokeWidth={1.5} />
              ))}
              {/* The combination's name, read bottom to top under its column */}
              <text
                transform={`translate(${colX(i)},${matrixBottom + 8}) rotate(-90)`}
                dy="0.32em"
                textAnchor="end"
                fontSize={11}
                fontWeight={on ? 700 : 400}
                fill={fg}
                opacity={on ? 1 : 0.8}
              >
                {c.id}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="upset-platforms-wrap">
        <button
          type="button"
          className="upset-platforms-pm"
          onClick={toggleDescriptions}
          aria-expanded={showDescriptions}
          aria-label={showDescriptions ? 'Hide platform descriptions' : 'Show platform descriptions'}
          title={showDescriptions ? 'Hide platform descriptions' : 'Show platform descriptions'}
        >
          {showDescriptions ? '−' : '+'}
        </button>
        {!showDescriptions && (
          <button type="button" className="upset-platforms-hint" onClick={toggleDescriptions}>
            Show platform descriptions
          </button>
        )}
        {showDescriptions && (
          <dl className="upset-platforms">
            {Object.keys(PLATFORM_DESCRIPTIONS).filter(r => rows.includes(r)).map(r => (
              <div key={r} className="upset-platform">
                <dt>{r}</dt>
                <dd>
                  {PLATFORM_DESCRIPTIONS[r]}
                  <span className="upset-platform-variants">
                    In the Atlas:{' '}
                    {Array.from(variants[r] || [])
                      .sort((a, b) => b[1] - a[1])
                      .map(([name, n]) => `${name === 'SomaScan' ? 'SomaScan, version not stated' : name} (${n})`)
                      .join(' · ')}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}
